// FE-SCRM-060 — <MoneyInput> dollars-with-cents → BigInt cents.
//
// Example:
//
//     const [revenue, setRevenue] = useState<bigint | null>(null);
//     <MoneyInput value={revenue} onChange={setRevenue} />
//     // value is BigInt cents (50000n = $500.00) so the form
//     // payload feeds the API's BigInt-cents shape directly.
//
// Stays a controlled input. Empty string → onChange(null). Invalid
// input doesn't fire onChange (so the user can keep typing toward
// a valid value) but the input gets aria-invalid for screen-reader
// affordance.

import { useEffect, useState } from "react";


const _NORMALIZE_RE = /[$\s,]/g;
const _VALID_RE = /^\d+(\.\d{0,2})?$/;


/**
 * Parse a dollars-with-cents string into BigInt cents.
 *
 * - empty / whitespace → ``null``
 * - valid (`123` or `123.4` or `123.45`) → BigInt cents
 * - invalid → ``undefined`` (sentinel; component holds the
 *   in-progress text but doesn't emit)
 */
export function parseDollarsToCents(
  raw: string,
): bigint | null | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const cleaned = trimmed.replace(_NORMALIZE_RE, "");
  if (!_VALID_RE.test(cleaned)) return undefined;
  const [dollarsStr, centsStr = ""] = cleaned.split(".");
  const dollars = BigInt(dollarsStr);
  const padded = (centsStr + "00").slice(0, 2);
  return dollars * 100n + BigInt(padded);
}


function _format(value: bigint | null | undefined): string {
  if (value === null || value === undefined) return "";
  const cents = value;
  const whole = cents / 100n;
  const frac = (cents % 100n + 100n) % 100n;
  return frac === 0n
    ? whole.toString()
    : `${whole.toString()}.${frac.toString().padStart(2, "0")}`;
}


export interface MoneyInputProps {
  value: bigint | null;
  onChange: (next: bigint | null) => void;
  currencySymbol?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}


export function MoneyInput({
  value, onChange, currencySymbol = "$",
  placeholder = "0.00",
  disabled, id, className,
}: MoneyInputProps): JSX.Element {
  const [text, setText] = useState(() => _format(value));
  const [invalid, setInvalid] = useState(false);

  // Sync the local text when the controlled value changes from
  // outside, but only when our local state hasn't been touched.
  useEffect(() => {
    const expected = _format(value);
    setText((current) => {
      const parsed = parseDollarsToCents(current);
      // Already parses to the same value? Don't stomp the user's
      // in-progress text (e.g. trailing ".0").
      if (parsed === value) return current;
      return expected;
    });
    setInvalid(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className={"relative inline-flex items-center " + (className ?? "")}>
      <span
        className={
          "absolute left-2 select-none text-sm text-gray-500"
        }
      >
        {currencySymbol}
      </span>
      <input
        type="text"
        role="textbox"
        inputMode="decimal"
        id={id}
        value={text}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          const parsed = parseDollarsToCents(next);
          if (parsed === undefined) {
            setInvalid(true);
            return;
          }
          setInvalid(false);
          onChange(parsed);
        }}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={
          "w-full rounded-md border bg-white py-1.5 pl-6 pr-2 "
          + "text-right text-sm tabular-nums "
          + "focus:border-indigo-500 focus:outline-none "
          + "focus:ring-1 focus:ring-indigo-500 "
          + (invalid
            ? "border-rose-500 focus:border-rose-500 focus:ring-rose-500 "
            : "border-gray-300 ")
        }
      />
    </div>
  );
}
